import { React, useState, useEffect, useMemo } from "react";
import { Typography, Box } from "@mui/material";
import RDataGrid from "../reusables/RDataGrid";
import PageLoader from "../loader/PageLoader";
import useAuth from "../hooks/useAuth";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import ReorderIcon from "@mui/icons-material/Reorder";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Add from "@mui/icons-material/Add";
import Button from "@mui/joy/Button";
import AddAdmin from "../forms/AddAdmin";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddPopup from "../popup/AddPopup";

import Notification from "../popup/Notification";
import ConfirmDialog from "../dialog/ConfirmDialog";
import { LightTooltip } from "../tooltip/ToolTip";

const Admins = () => {
  const [isFetchingAdmins, setIsFetchingAdmins] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [editAdmin, setEditAdmin] = useState(false);
  const [viewAdmin, setViewAdmin] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [refreshAdmin, setSetRefreshAdmins] = useState(null);

  const ADMINS_URL = "api/v1/users/tenant-managers?tenantId=";
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  // Delete tenant admin
  const onDelete = async (id) => {
    const controller = new AbortController();
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      const ADMINS_DELETE_URL = "api/v1/users/";
      const response = await axiosPrivate.delete(ADMINS_DELETE_URL + id, {
        signal: controller.signal,
      });
      var message = response.data.message;
      setNotify({
        isOpen: true,
        message: message,
        type: "success",
      });
      setSetRefreshAdmins(new Date());
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: "Failed To Delete Admin",
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: () => {
          onDelete(id);
        },
      });
    }
  };

  // Fetch tenant admins
  useEffect(() => {
    if (!isFetchingAdmins) {
      setIsFetchingAdmins(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getAdmins = async () => {
      try {
        const response = await axiosPrivate.get(
          `${ADMINS_URL}${auth.tenantId}&page=0&size=10`,
          {
            signal: controller.signal,
          },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        isMounted && setAdmins(response.data.data.managers);
        setIsFetchingAdmins(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingAdmins(false);
      }
    };

    getAdmins();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshAdmin]);

  const columns = useMemo(
    () => [
      {
        field: "rowNo", // from DataGrid rows defifnition
        headerName: "No.",
        sortable: false,
        headerClassName: "super-app-theme--header",
        minWidth: 87,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "firstName",
        headerName: "Name",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const name = `${params.row.firstName} ${params.row.lastName}`;
          return name;
        },
      },
      {
        field: "email",
        headerName: "Email",
        headerClassName: "super-app-theme--header",
        minWidth: 220,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "phone",
        headerName: "Phone",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "nationalId",
        headerName: "ID",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
      },
      {
        field: "emailVerified",
        headerName: "Email Verified",
        type: "boolean",
        headerClassName: "super-app-theme--header",
        minWidth: 120,
        flex: 1,
        align: "left",
        headerAlign: "left",
        editable: false,
        renderCell: function (params) {
          const emailVerified = params.row.emailVerified;
          return emailVerified ? "True" : "False";
        },
      },
      {
        field: "enabled",
        headerName: "Active",
        type: "boolean",
        headerClassName: "super-app-theme--header",
        minWidth: 87,
        flex: 1,
        align: "center",
        headerAlign: "center",
        editable: false,
        renderCell: function (params) {
          const enabled = params.row.enabled;
          return enabled ? "True" : "False";
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        headerClassName: "super-app-theme--header",
        align: "center",
        headerAlign: "center",
        minWidth: 120,
        flex: 1,
        sortable: false,
        disableClickEventBubbling: true,
        cellClassName: "actions",
        getActions: (params) => {
          return [
            <LightTooltip title="Edit admin">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={function () {
                  const currentRow = params.row;
                  const selectedAdmin = JSON.stringify(currentRow, null, 4);
                  setViewAdmin(false);
                  setEditAdmin(true);
                  setSelectedAdmin(selectedAdmin);
                  return setOpenPopup(true);
                }}
                color="success"
              />
            </LightTooltip>,
            <LightTooltip title="Delete admin">
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                color="error"
                onClick={function () {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Are you sure you want to delete this admin?",
                    subTitle: "You can't undo this operation",
                    action: "Delete",
                    onConfirm: () => {
                      onDelete(params.row.id);
                    },
                  });
                }}
              />
            </LightTooltip>,
            <GridActionsCellItem
              icon={<ReorderIcon />}
              label={"View More"}
              onClick={function () {
                const currentRow = params.row;
                const selectedAdmin = JSON.stringify(currentRow, null, 4);
                setViewAdmin(true);
                setEditAdmin(true); // You might want to edit admin under view admin
                setSelectedAdmin(selectedAdmin);
                return setOpenPopup(true);
              }}
              color="inherit"
              showInMenu
            />,
          ];
        },
      },
    ],
    []
  );

  // Close dialog upon successful speciality add/update
  const closeDialog = (message) => {
    setOpenPopup(false);
    setNotify({
      isOpen: true,
      message: message,
      type: "success",
    });
    setSetRefreshAdmins(new Date());
  };

  return isFetchingAdmins ? (
    <PageLoader title="Loading admins..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      {admins?.length ? (
        <Box
          sx={{
            height: "100%",
            width: "100%",
          }}
        >
          <div style={divStyle}>
            <Typography
              sx={{
                textAlign: "left",
                mt: 3,
                mb: 3,
                fontSize: {
                  lg: 30,
                  md: 20,
                  sm: 20,
                  xs: 19,
                },
              }}
            >
              Admins
            </Typography>

            <Button
              sx={{
                size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
              }}
              variant="solid"
              color="primary"
              onClick={function () {
                setEditAdmin(false);
                setSelectedAdmin(null);
                setOpenPopup(true);
              }}
              startDecorator={<Add />}
            >
              Admin
            </Button>
          </div>
          <Box
            sx={{
              //width: { xs: 480, sm: 480, md: 740, lg: 1218, xl: 1218 }, // xs=iPhone SE; sm=iPad Mini; md=iPad pro; lg=Nest Hub Max
              width: "100%",
              margin: "0 auto 16px",
              height: 560,
              overflow: "hidden",
            }}
          >
            <RDataGrid columns={columns} rows={admins} rowHeight={63} />
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewAdmin
                ? "View (Edit) Admin"
                : editAdmin
                ? "Edit (View) Admin"
                : "Add Admin"
            }
          >
            <AddAdmin
              closeDialog={closeDialog}
              editAdmin={editAdmin}
              selectedAdmin={selectedAdmin}
            />
          </AddPopup>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No admin to display
          </Typography>
          <Button
            sx={{
              size: { xs: "xs", sm: "xs", md: "md", lg: "lg", xl: "lg" },
            }}
            variant="solid"
            color="primary"
            onClick={function () {
              setEditAdmin(false);
              setSelectedAdmin(null);
              setOpenPopup(true);
            }}
            startDecorator={<Add />}
          >
            Tenant Admin
          </Button>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={
              viewAdmin
                ? "View (Edit) Admin"
                : editAdmin
                ? "Edit (View) Admin"
                : "Add Admin"
            }
          >
            <AddAdmin
              closeDialog={closeDialog}
              editAdmin={editAdmin}
              selectedAdmin={selectedAdmin}
            />
          </AddPopup>
        </div>
      )}
    </Box>
  );
};

export default Admins;
